<script lang="ts">
	import type { DisplayTotalLine, Order as GroceryOrder } from "proto/groceries_order";
  import type { Order as FoodOrder, Totals } from "proto/order";
	import type { Cart } from "$lib/def/food-cart";

  import { formatCurrency } from "$lib/util/format";
	import ToolTip, { ToolPosition } from "../shared/ToolTip.svelte";

	type Order = FoodOrder | GroceryOrder;

	interface Props {
		order: Order | Cart;
	}

	let { order }: Props = $props();

	/**
	 * Will only dipslay keys listed
	 * Excluded:
	 * subtotal
	 * processing_fee
	 * total
	 * call_centre_surcharge
	 * global_service_fee
	 * all_additional_fees
	 */
	const TitleFromKey = {
		delivery_fee: 'Delivery fee',
		driver_tip: 'Driver tip',
		discount: 'Coupon discount', // red
		food_discount: 'Discount', // red
		restaurant_donation: 'Restaurant donation',
		refund_credit_applied: 'Wallet credit applied', // red
		wallet_credit_applied: 'Wallet credit applied', // red (food cart)
		gift_value_applied: 'Gift value applied',
		wicode_value_applied: 'wiCode applied', // red
	}
	type TitleFromKeyOtion = typeof TitleFromKey[keyof typeof TitleFromKey];
		
	/**
	 * For use in cart and checkout
	 * Sorts, formats and adds styling to food order totals
	 * @param order or cart - passed in from template for reactivity
	 * @returns formatted array with styles
	 */
	function formatTotals(order: Order | Cart): DisplayTotalLine[] {

		/** if groc order */
		if ((order as GroceryOrder).display_totals) return (order as GroceryOrder).display_totals?.lines.filter(({ label, type }) =>
			type !== 'total' &&
			label?.toLowerCase() !== 'subtotal'
		) ?? [];

		const _arr = Object.keys(order.totals as Totals);
		const _return: DisplayTotalLine[] = [];

		_arr.forEach((key) => {
			/** Only add items from the list */
			if (TitleFromKey[key as keyof typeof TitleFromKey]) {

				const _new: DisplayTotalLine = {
					label: TitleFromKey[key as keyof typeof TitleFromKey],
					value: (order.totals as Totals)[key as keyof Totals],
					type: (
						key === 'discount' ||
						key === 'food_discount' ||
						key === 'refund_credit_applied' ||
						key === 'wallet_credit_applied' ||
						key === 'wicode_value_applied'
					) ? 'red' : '',
				};
				if (_new.type === 'red') _new.value = _new.value && -Math.abs(_new.value) || 0;

				// if (key === 'discount') _new.label = (order as FoodOrder).restaurant?.active_promotion?.display?.cart_line_title ?? 'Coupon discount';
				if (key === 'delivery_fee' && _new.value === 0) _new.label = 'Free delivery';
				if (key === 'food_discount') _new.label = (order as FoodOrder).restaurant?.active_promotion?.display?.cart_line_title ?? 'Discount';

				if (_new.label?.startsWith('FOOD ')) _new.label = _new.label.substring(5, _new.label.length);

				if (key === 'discount' || (key === 'food_discount' && (order.totals as Totals)[key as keyof Totals] !== 0)) {
					_return.unshift(_new);
				// } else if ((key !== 'driver_tip' && key !== 'delivery_fee') || ((key as keyof Totals) === 'food_discount' && (order as FoodOrder).restaurant?.active_promotion) || (order.totals as Totals)[key] > 0) { // display driver tip only if greater than zero
				} else if (key === 'delivery_fee' || (order.totals as Totals)[key as keyof Totals] !== 0) { // only shows none zero values - except for discount (not food_discount) which can be a zero value free delivery
					_return.push(_new);
				}
			}
		});

		// if ((order as FoodOrder).restaurant.)
		if (Array.isArray((order as FoodOrder).additional_fees)) {
			(order as FoodOrder).additional_fees.forEach((add) => {
				_return.push({
					label: add.display?.label ?? add.display?.description ?? 'Additional fee',
					value: add.fee,
					type: '',
				});
			});
		}

		return _return;
	}
</script>

<table>
	<tbody>
			<tr>
				<td class="label-m book">Subtotal</td>
				<td class="label-m bold text-right">{formatCurrency(order?.totals?.subtotal)}</td>
			</tr>
			<!-- <br>
			{JSON.stringify(formatTotals(order))}
			<br> -->
		{#each formatTotals(order) as total}
			<tr class={total.type} style="position: relative;">
				<td class="label-m book">
					{total.label}

					<!-- TODO: @Michiel -> Find out what the copy is for the delivery fee tooltip. Neither design nor apps shows this tooltip -->
						<!-- {#if total.label === 'Delivery fee'}
						<span class="tip">?
							<ToolTip short message='...' pos={ToolPosition.BC} />
						</span>
						{/if} -->

						{#if total.label === 'Wallet credit applied'}
						<span class="tip">?
							<ToolTip short message='This credit is automatically deducted from your total when the order is placed. Any used credit can be viewed in your wallet.' pos={ToolPosition.BC} />
						</span>
						{/if}

						{#if total.label === 'Backup Allowance'}
						<span class="tip">?
							<ToolTip short message='Because availability of products is always changing, we’ll charge you for the most expensive one of the two. After your item has been purchased, we’ll refund you the difference to your credit wallet.' pos={ToolPosition.BC} />
						</span>
						{/if}

				</td>
				<td class="label-m bold text-right">{formatCurrency(total.value)}</td>
			</tr>
		{/each}
	</tbody>
</table>

<style lang="scss">
	@use 'color';
	.tip {
		position: relative;
		cursor: pointer;
		border: 1px solid color.$on-surface;
    border-radius: 50%;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    min-width: 12px;
    height: 12px;
    font-size: 0.8rem;
    margin-left: 0.3rem;
	}
</style>